import { useEffect } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import {
  AiNodeQuota,
  BasicModelCard,
  SimulatedQuotasResponse,
  SimulateQuotasRequest,
} from "src/aiNode/types";
import {
  ConnectionsEndpoint,
  IntegrationBuilderEndpoint,
  LlmInferenceEndpoint,
  ManifestConnectionsEndpoint,
  ManifestsEndpoint,
  ProviderEndpoint,
} from "src/api/connectApi/endpoints";
import {
  ManifestCatalogEntry,
  ManifestIntegrationProvider,
} from "src/api/connectApi/manifestTypes";
import {
  Connection,
  ConnectionCreate,
  MockTemplateRequest,
  MockTemplateResponse,
  RetoolEmbedResponse,
  ValidateCustomAuthRequest,
  ValidateCustomAuthResponse,
  ValidateCustomErrorProcessingRequest,
  ValidateCustomErrorProcessingResponse,
} from "src/api/connectApi/types";
import { providerSdkApi, providerSdkBundlesApi } from "src/api/endpoints";
import { NEVER_REFETCH_OPTIONS } from "src/api/queries";
import {
  ProviderSdkBundleDBCreate,
  ProviderSdkBundleDBUpdate,
  ProviderSdkBundleResponse,
  ProviderSdkCatalogEntry,
  ProviderSdkConfigDBCreate,
  ProviderSdkConfigDBUpdate,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import {
  ManifestConnection,
  ValidateProductionEnvironmentResponse,
} from "src/connections/config/manifest/types";
import {
  isCaseCreationNodeConnection,
  isCaseUpdateNodeConnection,
  isManualReviewNodeConnection,
} from "src/connections/model/inboundWebhook";
import { queryClient } from "src/queryClient";
import { shouldShowInternalResources } from "src/router/featureFlags";

export const useAvailableAiModels = (baseUrl: string | undefined) => {
  return useQuery<BasicModelCard[], Error>({
    queryKey: ["/llm-inference/models", baseUrl],
    queryFn: () => LlmInferenceEndpoint.all(baseUrl),
    enabled: Boolean(baseUrl),
  });
};

export const useAiNodeQuotas = (
  baseUrl: string | undefined,
  options: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery<AiNodeQuota, Error>({
    queryKey: ["/llm-inference/quotas", baseUrl],
    queryFn: () => LlmInferenceEndpoint.quotas(baseUrl),
    enabled: Boolean(baseUrl) && options.enabled,
  });
};

export const useSimulatedQuotas = (
  baseUrl: string | undefined,
  payload: SimulateQuotasRequest | undefined,
) => {
  return useQuery<SimulatedQuotasResponse, Error>({
    queryKey: ["/llm-inference/quotas/simulations", baseUrl, payload],
    queryFn: () => LlmInferenceEndpoint.simulateQuotas(baseUrl, payload!),
    enabled: Boolean(baseUrl) && Boolean(payload),
    // Keep previous data only within the same workspace, so that switching
    // workspaces shows a loading state instead of the other workspace's quotas
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === baseUrl ? previousData : undefined,
    retry: false,
  });
};

export const useConnection = ({
  baseUrl,
  id,
  neverRefetch = false,
}: {
  baseUrl: string | undefined;
  id: string | undefined;
  neverRefetch?: boolean;
}) => {
  return useQuery<Connection, Error>({
    queryKey: ["connections", baseUrl, id],
    queryFn: () => {
      if (baseUrl == null || id == null) {
        throw new Error("Invalid baseUrl or id");
      }
      return ConnectionsEndpoint.get(baseUrl, id);
    },
    enabled: Boolean(baseUrl) && Boolean(id),
    ...(neverRefetch && NEVER_REFETCH_OPTIONS),
  });
};

const setInternalConnectionsFirst = (data: Connection[]) => {
  const caseCreationConnection = data.find(isCaseCreationNodeConnection);
  const caseUpdateConnection = data.find(isCaseUpdateNodeConnection);
  const manualReviewConnection = data.find(isManualReviewNodeConnection);
  let filteredData = data.filter(
    (connection) =>
      connection.id !== manualReviewConnection?.id &&
      connection.id !== caseCreationConnection?.id &&
      connection.id !== caseUpdateConnection?.id,
  );

  if (manualReviewConnection) {
    filteredData = [manualReviewConnection, ...filteredData];
  }

  if (caseUpdateConnection) {
    filteredData = [caseUpdateConnection, ...filteredData];
  }

  if (caseCreationConnection) {
    filteredData = [caseCreationConnection, ...filteredData];
  }

  return filteredData;
};

const hideInternalConnections = (data: Connection[]) => {
  return data.filter(
    (connection) =>
      !isManualReviewNodeConnection(connection) &&
      !isCaseCreationNodeConnection(connection) &&
      !isCaseUpdateNodeConnection(connection),
  );
};

type ConnectionOptions = {
  /**
   * We don't want to show manual review/case management connection
   * in the list of connections on connections page
   */
  hideInternalConnections?: boolean;
  showInternalResources?: boolean;
};

export const useConnections = (
  baseUrl?: string,
  options?: ConnectionOptions,
) => {
  return useQuery<Connection[], Error>({
    queryKey: ["connections", baseUrl, options?.showInternalResources],
    queryFn: () =>
      ConnectionsEndpoint.all(baseUrl, {
        include_internal_connections:
          shouldShowInternalResources() || options?.showInternalResources,
      }),
    enabled: Boolean(baseUrl),
    select: options?.hideInternalConnections
      ? hideInternalConnections
      : setInternalConnectionsFirst,
  });
};

export const prefetchConnections = (baseUrl: string) => {
  return queryClient.prefetchQuery({
    queryKey: ["connections", baseUrl],
    queryFn: () =>
      ConnectionsEndpoint.all(baseUrl, {
        include_internal_connections: shouldShowInternalResources(),
      }),
    gcTime: Infinity,
  });
};

export const useCreateConnection = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<Connection, Error, ConnectionCreate>({
    mutationFn: (data) => ConnectionsEndpoint.create(baseUrl, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl],
      });
    },
  });
};

export const useCreateConnectionAndResources = (baseUrl: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<
      ReturnType<(typeof ConnectionsEndpoint)["createConnectionAndResources"]>
    >,
    Error,
    Parameters<(typeof ConnectionsEndpoint)["createConnectionAndResources"]>[1]
  >({
    mutationFn: (data) =>
      ConnectionsEndpoint.createConnectionAndResources(baseUrl, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl],
      });
    },
  });
};

type EditParams = {
  id: string;
  payload: ConnectionCreate;
};

export const useEditConnection = (
  baseUrl: string | undefined,
  id: string | undefined,
) => {
  const queryClient = useQueryClient();
  return useMutation<Connection, AxiosError, EditParams>({
    mutationFn: ({ id, payload }) =>
      ConnectionsEndpoint.update(baseUrl, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl, id],
      });
    },
  });
};

type EditConnectionAndResourcesParams = {
  id: string;
  payload: Parameters<
    (typeof ConnectionsEndpoint)["updateConnectionAndResources"]
  >[2];
};

export const useEditConnectionAndResources = (baseUrl: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<
      ReturnType<(typeof ConnectionsEndpoint)["updateConnectionAndResources"]>
    >,
    Error,
    EditConnectionAndResourcesParams
  >({
    mutationFn: ({ id, payload }) =>
      ConnectionsEndpoint.updateConnectionAndResources(baseUrl, id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl, data.id],
      });
    },
  });
};

export const useDeleteConnection = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      ConnectionsEndpoint.delete(baseUrl, connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl],
      });
    },
  });
};

type ProbeResponse = {
  connectionId?: string;
  status: AxiosResponse["status"];
};

export const useConnectionStatus = (
  baseUrl: string | undefined,
  connection: Connection | undefined,
  {
    onTestPass,
    onTestFail,
  }: {
    onTestPass?: (connection?: Connection) => void;
    onTestFail?: (connection?: Connection, error?: unknown) => void;
  } = {},
) => {
  const query = useQuery<ProbeResponse>({
    queryKey: ["probe", baseUrl, connection?.id],
    queryFn: async () => {
      const result = await ConnectionsEndpoint.test(baseUrl, connection?.id);
      return { status: result.status };
    },
    enabled: Boolean(baseUrl) && Boolean(connection),
    retry: false,
    throwOnError: false,
  });

  useEffect(() => {
    if (connection) {
      if (query.error) {
        onTestFail?.(connection, query.error);
        return;
      }

      if (query.data) {
        onTestPass?.(connection);
      }
    }
  }, [query.data, query.error, connection, onTestPass, onTestFail]);

  return query;
};

export const useTestConnection = (baseUrl: string | undefined) => {
  return useMutation<ProbeResponse, Error, string>({
    mutationFn: (connectionId: string) => {
      return ConnectionsEndpoint.test(baseUrl, connectionId);
    },
    throwOnError: false,
  });
};

export const useRetoolEmbed = (
  baseUrl: string | undefined,
  connection: Connection | undefined,
  userId: string | undefined,
  email: string | undefined,
) => {
  return useQuery<RetoolEmbedResponse, Error>({
    queryKey: ["retool", "retool_embed", baseUrl],
    queryFn: () =>
      ProviderEndpoint.query<RetoolEmbedResponse>(
        baseUrl,
        "retool",
        "retool_embed",
        {
          provider_args: { max_age_seconds: 0 },
          query: { user_id: userId, email: email },
          resource_config_id: connection?.resource_configs.find(
            (rc) => rc.resource === "retool_embed",
          )?.id,
        },
      ),
    enabled:
      Boolean(baseUrl) &&
      Boolean(connection) &&
      Boolean(userId) &&
      Boolean(email),
  });
};

const MANIFEST_CATALOG_STALE_TIME_MS = 5 * 60 * 1000;

export const useManifests = (baseUrl: string | undefined) => {
  return useQuery<ManifestIntegrationProvider[], Error>({
    queryKey: ["/manifests?latest=true", baseUrl],
    queryFn: () => ManifestsEndpoint.all(baseUrl),
    enabled: Boolean(baseUrl),
  });
};

export const useLocalManifests = (baseUrl: string | undefined) => {
  return useQuery<ManifestCatalogEntry[], Error>({
    queryKey: ["/manifests/local", baseUrl],
    queryFn: () => ManifestsEndpoint.local(baseUrl),
    enabled: Boolean(baseUrl),
  });
};

export const useManifestCatalog = (baseUrl: string | undefined) => {
  return useQuery<ManifestCatalogEntry[], Error>({
    queryKey: ["/manifests/catalog", baseUrl],
    queryFn: () => ManifestsEndpoint.catalog(baseUrl),
    enabled: Boolean(baseUrl),
    staleTime: MANIFEST_CATALOG_STALE_TIME_MS,
  });
};

export const useProviderManifest = (
  baseUrl: string | undefined,
  provider: string | undefined,
  manifest_version: string | undefined,
) => {
  return useQuery<ManifestIntegrationProvider, Error>({
    queryKey: ["/manifests", baseUrl, provider, manifest_version],
    queryFn: () =>
      ManifestsEndpoint.get(provider as string, manifest_version, baseUrl),
    enabled: Boolean(baseUrl) && Boolean(provider) && Boolean(manifest_version),
    // a published manifest version is immutable; only "latest" can change
    staleTime:
      manifest_version === "latest" ? MANIFEST_CATALOG_STALE_TIME_MS : Infinity,
  });
};

export const useCreateManifestConnection = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<Connection, Error, ManifestConnection>({
    mutationFn: (data) => ManifestConnectionsEndpoint.create(baseUrl, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl],
      });
    },
  });
};

type EditManifestConnectionParams = {
  connectionId: string;
  payload: ManifestConnection;
};

export const useUpdateManifestConnection = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<Connection, Error, EditManifestConnectionParams>({
    mutationFn: ({ connectionId, payload }) =>
      ManifestConnectionsEndpoint.update(baseUrl, connectionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connections", baseUrl],
      });
    },
  });
};

export const useValidateProductionEnvironment = (
  baseUrl: string | undefined,
  connection_ids: string[],
) => {
  return useQuery<ValidateProductionEnvironmentResponse, Error>({
    queryKey: [baseUrl, connection_ids],
    queryFn: () =>
      ConnectionsEndpoint.validateProductionEnvironment(baseUrl, {
        connection_ids: connection_ids,
      }),
    enabled: Boolean(baseUrl) && Boolean(connection_ids),
  });
};

export const useMockTemplate = (baseUrl: string | undefined) => {
  return useMutation<MockTemplateResponse, Error, MockTemplateRequest>({
    mutationFn: (data) =>
      IntegrationBuilderEndpoint.mockTemplate(baseUrl, data),
  });
};

export const useValidateCustomAuth = (baseUrl: string | undefined) => {
  return useMutation<
    ValidateCustomAuthResponse,
    Error,
    ValidateCustomAuthRequest
  >({
    mutationFn: (data) =>
      IntegrationBuilderEndpoint.validateCustomAuth(baseUrl, data),
  });
};

export const useValidateCustomErrorProcessing = (
  baseUrl: string | undefined,
) => {
  return useMutation<
    ValidateCustomErrorProcessingResponse,
    Error,
    ValidateCustomErrorProcessingRequest
  >({
    mutationFn: (data) =>
      IntegrationBuilderEndpoint.validateCustomErrorProcessing(baseUrl, data),
  });
};

const SDK_CATALOG_KEY = "sdk-catalog";
const SDK_CONFIGS_KEY = "sdk-configs";
const SDK_BUNDLES_KEY = "sdk-bundles";

const requireBaseUrl = (baseUrl: string | undefined): string => {
  if (!baseUrl) throw new Error("No baseUrl provided");
  return baseUrl;
};

export const useSdkCatalog = (baseUrl: string | undefined) => {
  return useQuery<ProviderSdkCatalogEntry[], Error>({
    queryKey: [SDK_CATALOG_KEY, baseUrl],
    queryFn: async () => {
      const response = await providerSdkApi(
        requireBaseUrl(baseUrl),
      ).getProviderSdkCatalog();
      return response.data.data;
    },
    enabled: Boolean(baseUrl),
    // the catalog only changes with a connect deploy
    staleTime: MANIFEST_CATALOG_STALE_TIME_MS,
  });
};

export const useSdkConfigs = (
  baseUrl: string | undefined,
  options: { enabled?: boolean } = {},
) => {
  return useQuery<ProviderSdkConfigResponse[], Error>({
    queryKey: [SDK_CONFIGS_KEY, baseUrl],
    queryFn: async () => {
      const response = await providerSdkApi(
        requireBaseUrl(baseUrl),
      ).listProviderSdkConfigs();
      return response.data.data;
    },
    enabled: Boolean(baseUrl) && (options.enabled ?? true),
    // The global default sends 500s to the error boundary, which would take the
    // whole Connections page down. The SDK surface degrades instead: callers
    // read `isError` and keep rendering without SDK rows.
    throwOnError: false,
  });
};

export const useCreateSdkConfig = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<
    ProviderSdkConfigResponse,
    Error,
    ProviderSdkConfigDBCreate
  >({
    mutationFn: async (data) => {
      const response = await providerSdkApi(
        requireBaseUrl(baseUrl),
      ).createProviderSdkConfig({
        providerSdkConfigDBCreate: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SDK_CONFIGS_KEY, baseUrl] });
    },
  });
};

type UpdateSdkConfigParams = {
  id: string;
  payload: ProviderSdkConfigDBUpdate;
};

export const useUpdateSdkConfig = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<ProviderSdkConfigResponse, Error, UpdateSdkConfigParams>({
    mutationFn: async ({ id, payload }) => {
      const response = await providerSdkApi(
        requireBaseUrl(baseUrl),
      ).updateProviderSdkConfig({
        providerSdkConfigId: id,
        providerSdkConfigDBUpdate: payload,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SDK_CONFIGS_KEY, baseUrl] });
      queryClient.invalidateQueries({ queryKey: [SDK_BUNDLES_KEY, baseUrl] });
    },
  });
};

export const useDeleteSdkConfig = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: async (id) => {
      await providerSdkApi(requireBaseUrl(baseUrl)).deleteProviderSdkConfig({
        providerSdkConfigId: id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SDK_CONFIGS_KEY, baseUrl] });
      queryClient.invalidateQueries({ queryKey: [SDK_BUNDLES_KEY, baseUrl] });
    },
  });
};

export const useSdkBundles = (
  baseUrl: string | undefined,
  options: { enabled?: boolean } = {},
) => {
  return useQuery<ProviderSdkBundleResponse[], Error>({
    queryKey: [SDK_BUNDLES_KEY, baseUrl],
    queryFn: async () => {
      const response = await providerSdkBundlesApi(
        requireBaseUrl(baseUrl),
      ).listProviderSdkBundles();
      return response.data.data;
    },
    enabled: Boolean(baseUrl) && (options.enabled ?? true),
    // See useSdkConfigs: an SDK outage must not take the Connections page down.
    throwOnError: false,
  });
};

export const useCreateSdkBundle = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<
    ProviderSdkBundleResponse,
    Error,
    ProviderSdkBundleDBCreate
  >({
    mutationFn: async (data) => {
      const response = await providerSdkBundlesApi(
        requireBaseUrl(baseUrl),
      ).createProviderSdkBundle({ providerSdkBundleDBCreate: data });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SDK_BUNDLES_KEY, baseUrl] });
    },
  });
};

type UpdateSdkBundleParams = {
  id: string;
  payload: ProviderSdkBundleDBUpdate;
};

export const useUpdateSdkBundle = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<ProviderSdkBundleResponse, Error, UpdateSdkBundleParams>({
    mutationFn: async ({ id, payload }) => {
      const response = await providerSdkBundlesApi(
        requireBaseUrl(baseUrl),
      ).updateProviderSdkBundle({
        providerSdkBundleId: id,
        providerSdkBundleDBUpdate: payload,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SDK_BUNDLES_KEY, baseUrl] });
    },
  });
};

export const useDeleteSdkBundle = (baseUrl: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: async (id) => {
      await providerSdkBundlesApi(
        requireBaseUrl(baseUrl),
      ).deleteProviderSdkBundle({
        providerSdkBundleId: id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SDK_BUNDLES_KEY, baseUrl] });
    },
  });
};
